import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { formatDateString } from 'utilities/time'
import {
  useCampaignList,
  GET_OUTREACH_HISTORY_LIST,
  useOutreachCampaignStats,
} from '../../graphql/campaignQueries'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'
import { TEMPLATE_CHANNELS } from '../../consts'
import TemplatePreview from '../TemplatesTab/TemplatePreview'
import { getHistoryStepLabel } from '../../stepLabels'
import {
  InfoTooltip,
  OutreachStatsCard,
} from '../../components/OutreachStatsCard'

const channelLabel = (channel) =>
  TEMPLATE_CHANNELS.find((c) => c.value === channel)?.label ?? channel

const CampaignRow = ({
  campaign,
  selected,
  onToggleSelect,
  onNavigate,
  onStats,
}) => {
  const { data } = useOutreachCampaignStats(campaign.id)
  const s = data?.outreachCampaignStats

  useEffect(() => {
    if (s) onStats(campaign.id, s)
  }, [campaign.id, s, onStats])

  const fmt = (d) => formatDateString({ dateString: d, format: 'MMM DD, YYYY' })

  let dateRange = '-'
  if (campaign.startDate && campaign.endDate)
    dateRange = `${fmt(campaign.startDate)} – ${fmt(campaign.endDate)}`
  else if (campaign.startDate) dateRange = `Starting ${fmt(campaign.startDate)}`
  else if (campaign.endDate) dateRange = `Ending ${fmt(campaign.endDate)}`

  return (
    <TableRow key={campaign.id} hover sx={{ cursor: 'pointer' }}>
      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggleSelect(campaign.id)}
        />
      </TableCell>
      <TableCell onClick={onNavigate}>
        <Typography variant="body2" fontWeight="medium">
          {campaign.name}
        </Typography>
      </TableCell>
      <TableCell align="center" onClick={onNavigate}>
        {campaign.priority}
      </TableCell>
      <TableCell align="center" onClick={onNavigate}>
        {dateRange}
      </TableCell>
      <TableCell align="center" onClick={onNavigate}>
        {s ? (
          <>
            <Typography variant="body2" color="success.main" component="span">
              {s.sent}
            </Typography>
            {s.totalMessages > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
              >
                {` (${Math.round((s.sent / s.totalMessages) * 100)}%)`}
              </Typography>
            )}
          </>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell align="center" onClick={onNavigate}>
        {s
          ? (() => {
              const notSent = s.failed + s.undeliverable + s.unsubscribed
              return (
                <>
                  <Typography
                    variant="body2"
                    color={notSent > 0 ? 'error.main' : 'text.primary'}
                    component="span"
                  >
                    {notSent}
                  </Typography>
                  {s.totalMessages > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                    >
                      {` (${Math.round((notSent / s.totalMessages) * 100)}%)`}
                    </Typography>
                  )}
                </>
              )
            })()
          : '—'}
      </TableCell>
      <TableCell align="center" onClick={onNavigate}>
        {s ? `${s.uniqueDelivered ?? 0} / ${s.uniqueAttempted ?? 0}` : '—'}
      </TableCell>
    </TableRow>
  )
}

const OutreachDashboard = () => {
  const navigate = useNavigate()
  const [history, setHistory] = useState([])
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(new Set())
  const [campaignStatsMap, setCampaignStatsMap] = useState({})
  const [previewTemplate, setPreviewTemplate] = useState(null)

  const { loading: loadingCampaigns, data: campaignsData } = useCampaignList()
  const { data: statsData } = useOutreachCampaignStats(null)

  const [fetchHistory, { loading: loadingHistory }] = useLazyQuery(
    GET_OUTREACH_HISTORY_LIST,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setHistory(data?.outreachHistoryList?.history || [])
      },
    },
  )

  useEffect(() => {
    fetchHistory({ variables: { filters: {}, limit: 50 } })
  }, [fetchHistory])

  const campaigns = useMemo(
    () => campaignsData?.campaignList?.campaigns || [],
    [campaignsData],
  )

  const [today] = useState(() => new Date().toISOString().split('T')[0])
  const activeCampaigns = useMemo(
    () =>
      campaigns
        .filter((c) => c.active && (!c.endDate || c.endDate >= today))
        .sort((a, b) => (b.priority ?? -Infinity) - (a.priority ?? -Infinity)),
    [campaigns, today],
  )

  const templateByContentId = useMemo(
    () =>
      Object.fromEntries(
        campaigns.flatMap((c) =>
          (c.campaignContent || []).map((cc) => [cc.id, cc.template ?? null]),
        ),
      ),
    [campaigns],
  )

  const stepLabelByContentId = useMemo(
    () =>
      Object.fromEntries(
        campaigns.flatMap((c) => {
          const sorted = [...(c.campaignContent || [])].sort(
            (a, b) => a.sequence - b.sequence,
          )
          return sorted.map((cc) => [cc.id, cc.template?.name ?? cc.templateId])
        }),
      ),
    [campaigns],
  )

  const globalStats = statsData?.outreachCampaignStats ?? null

  const handleToggleSelect = useCallback((campaignId) => {
    setSelectedCampaignIds((prev) => {
      const next = new Set(prev)
      if (next.has(campaignId)) next.delete(campaignId)
      else next.add(campaignId)
      return next
    })
  }, [])

  const handleStatsReported = useCallback((campaignId, s) => {
    setCampaignStatsMap((prev) => ({ ...prev, [campaignId]: s }))
  }, [])

  const displayStats = useMemo(() => {
    if (selectedCampaignIds.size === 0) return globalStats
    const selected = [...selectedCampaignIds].map((id) => campaignStatsMap[id])
    if (selected.some((s) => !s)) return null
    return selected.reduce(
      (acc, s) => ({
        totalMessages: acc.totalMessages + (s.totalMessages ?? 0),
        sent: acc.sent + (s.sent ?? 0),
        failed: acc.failed + (s.failed ?? 0),
        undeliverable: acc.undeliverable + (s.undeliverable ?? 0),
        unsubscribed: acc.unsubscribed + (s.unsubscribed ?? 0),
        uniqueAttempted: acc.uniqueAttempted + (s.uniqueAttempted ?? 0),
        uniqueDelivered: acc.uniqueDelivered + (s.uniqueDelivered ?? 0),
      }),
      {
        totalMessages: 0,
        sent: 0,
        failed: 0,
        undeliverable: 0,
        unsubscribed: 0,
        uniqueAttempted: 0,
        uniqueDelivered: 0,
      },
    )
  }, [selectedCampaignIds, campaignStatsMap, globalStats])

  const multiSelected = selectedCampaignIds.size > 0

  if (loadingCampaigns && !campaigns.length) return <Loading />

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          Outreach Dashboard
        </Typography>
      </AdminPageHeader>

      {/* Summary stats */}
      <Box sx={{ mb: 1, minHeight: 24 }}>
        {selectedCampaignIds.size > 0 && (
          <Typography variant="body2" color="text.secondary">
            Showing totals for {selectedCampaignIds.size} selected campaign
            {selectedCampaignIds.size !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'stretch' }}>
        <Paper
          elevation={1}
          sx={{
            p: 2.5,
            flex: 2,
            textAlign: 'center',
            position: 'relative',
            opacity: multiSelected ? 0.4 : 1,
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Distinct patients with at least one successfully delivered message (sent status) out of all patients attempted (any status).">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography
            variant="h4"
            color={multiSelected ? 'text.disabled' : undefined}
          >
            {multiSelected
              ? '—'
              : displayStats
              ? `${displayStats.uniqueDelivered} / ${displayStats.uniqueAttempted}`
              : '—'}
          </Typography>
          <Typography
            variant="caption"
            color="success.main"
            sx={{
              display: 'block',
              visibility:
                !multiSelected && displayStats?.uniqueAttempted > 0
                  ? 'visible'
                  : 'hidden',
            }}
          >
            {!multiSelected && displayStats?.uniqueAttempted > 0
              ? `${Math.round(
                  (displayStats.uniqueDelivered /
                    displayStats.uniqueAttempted) *
                    100,
                )}%`
              : '0%'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {multiSelected ? (
              <Tooltip title="Cannot be summed across campaigns — the same patient may appear in multiple.">
                <span>Patients Reached</span>
              </Tooltip>
            ) : (
              'Patients Reached'
            )}
          </Typography>
        </Paper>
        <OutreachStatsCard
          value={displayStats?.sent ?? '—'}
          label="Messages Delivered"
          color="success.main"
          caption={
            displayStats?.totalMessages > 0
              ? `${Math.round(
                  (displayStats.sent / displayStats.totalMessages) * 100,
                )}%`
              : undefined
          }
          infoTooltip="Total messages successfully delivered (sent status). One patient may account for multiple messages across content steps or channels."
        />
        <OutreachStatsCard
          value={displayStats?.failed ?? '—'}
          label="Failed"
          color={displayStats?.failed > 0 ? 'error.main' : undefined}
          caption={
            displayStats?.totalMessages > 0
              ? `${Math.round(
                  (displayStats.failed / displayStats.totalMessages) * 100,
                )}%`
              : undefined
          }
          infoTooltip="Messages that failed to send due to a transient or unclassified error. May succeed on retry."
        />
        <OutreachStatsCard
          value={displayStats?.undeliverable ?? '—'}
          label="Undeliverable"
          caption={
            displayStats?.totalMessages > 0
              ? `${Math.round(
                  (displayStats.undeliverable / displayStats.totalMessages) *
                    100,
                )}%`
              : undefined
          }
          infoTooltip="Messages where delivery is permanently impossible: the patient has no contact record in Intercom, or the email hard-bounced at the provider."
        />
        <OutreachStatsCard
          value={displayStats?.unsubscribed ?? '—'}
          label="Unsubscribed"
          caption={
            displayStats?.totalMessages > 0
              ? `${Math.round(
                  (displayStats.unsubscribed / displayStats.totalMessages) *
                    100,
                )}%`
              : undefined
          }
          infoTooltip="Messages that failed to send because the patient was already unsubscribed at the time of sending. This is not the count of patients who unsubscribed as a result of receiving a message — Intercom tracks that separately."
        />
      </Box>

      {/* Active campaigns */}
      <Card elevation={0} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6">
                Active Campaigns ({activeCampaigns.length})
              </Typography>
              {selectedCampaignIds.size > 0 && (
                <Chip
                  label={`${selectedCampaignIds.size} selected`}
                  size="small"
                  onDelete={() => setSelectedCampaignIds(new Set())}
                />
              )}
            </Box>
            <Button
              type="secondary"
              size="small"
              onClick={() =>
                navigate(ROUTE_METADATA.adminOutreachCampaigns.path)
              }
            >
              View All Campaigns
            </Button>
          </Box>
          {activeCampaigns.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active campaigns
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Priority</TableCell>
                    <TableCell align="center">Date Range</TableCell>
                    <TableCell align="center">Sent</TableCell>
                    <TableCell align="center">
                      <InfoTooltip title="Sum of failed, undeliverable, and unsubscribed messages. See the campaign dashboard for a per-status breakdown.">
                        <span style={{ cursor: 'help' }}>Not Sent</span>
                      </InfoTooltip>
                    </TableCell>
                    <TableCell align="center">Patients Reached</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeCampaigns.map((campaign) => (
                    <CampaignRow
                      key={campaign.id}
                      campaign={campaign}
                      selected={selectedCampaignIds.has(campaign.id)}
                      onToggleSelect={handleToggleSelect}
                      onNavigate={() =>
                        navigate(
                          `${ROUTE_METADATA.adminOutreachCampaigns.path}/${campaign.id}`,
                        )
                      }
                      onStats={handleStatsReported}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Recent outreach history */}
      <Card elevation={0}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Recent Outreach Activity
          </Typography>
          {loadingHistory && history.length === 0 ? (
            <Loading />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No outreach history yet
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Patient ID</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => {
                    const campaign = campaigns.find(
                      (c) => c.id === item.campaignId,
                    )
                    const template = item.campaignContentId
                      ? templateByContentId[item.campaignContentId]
                      : null
                    const stepLabel = item.campaignContentId
                      ? stepLabelByContentId[item.campaignContentId]
                      : null
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {item.patientId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() =>
                              navigate(
                                `${ROUTE_METADATA.adminOutreachCampaigns.path}/${item.campaignId}`,
                              )
                            }
                          >
                            {campaign?.name || item.campaignId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.campaignContentId ? (
                            template ? (
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.7 },
                                }}
                                onClick={() => setPreviewTemplate(template)}
                              >
                                {stepLabel}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {getHistoryStepLabel(item)}
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={channelLabel(item.channel)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.deliveryStatus}
                            size="small"
                            color={
                              item.deliveryStatus === 'sent'
                                ? 'success'
                                : item.deliveryStatus === 'failed'
                                ? 'error'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {item.sentAt
                            ? new Date(item.sentAt * 1000).toLocaleString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <TemplatePreview
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onOpenFull={
          previewTemplate
            ? () => {
                navigate(
                  `${ROUTE_METADATA.adminOutreachTemplates.path}/${previewTemplate.id}`,
                )
                setPreviewTemplate(null)
              }
            : undefined
        }
      />
    </>
  )
}

export default OutreachDashboard
